import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useQueryClient } from '@tanstack/react-query'
import { syncInstallation } from 'src/Libs/flow/installation/api'
import { ChargingMode, ChargingSchedule, EebusChargingStation, ModbusTcpChargingStation } from 'src/Libs/flow/installation/forms'
import { installationDevicesKey } from 'src/Libs/flow/installation/hooks'
import { ControlledModbusTcpPUTDto, ControlledEebusPUTDto, updateEvse } from '../api/evse.api.js'
import { usePortalMutation } from 'src/Libs/api'

type EvseType = ModbusTcpChargingStation | EebusChargingStation
type DtoType = ControlledModbusTcpPUTDto | ControlledEebusPUTDto

type UseEvseChargingMutationParams<TEvse extends EvseType, TDto extends DtoType> = {
  installationId: number
  evse: TEvse
  getDtoFn: (evse: TEvse) => TDto
}

type UseEvseChargingMutationResult = {
  isPending: boolean
  handleChargingModeChange: (newMode: ChargingMode, schedules: { safePv: ChargingSchedule; efficientPv: ChargingSchedule }) => void
  handleSafePvScheduleChange: (schedule: ChargingSchedule) => void
  handleEfficientPvScheduleChange: (schedule: ChargingSchedule) => void
}

export const useEvseChargingPriority = <TEvse extends EvseType, TDto extends DtoType>({
  installationId,
  evse,
  getDtoFn,
}: UseEvseChargingMutationParams<TEvse, TDto>): UseEvseChargingMutationResult => {
  const { t } = useCustomTranslation()
  const { enqueueErrorStack } = useSnackbar()
  const authFetch = useAuthFetchWithResult()
  const queryClient = useQueryClient()

  const mutateEvse = usePortalMutation({
    mutationFn: (newEvse: TDto) =>
      updateEvse(installationId, evse.rawData?.deviceLinkId?.toString() ?? '', evse.rawData?.id?.toString() ?? '', newEvse, authFetch).then(() =>
        syncInstallation(installationId, authFetch)
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: installationDevicesKey(installationId) }),
    onError: () => enqueueErrorStack(t('common.update-failed-error')),
  })

  const handleChargingModeChange = (newMode: ChargingMode, schedules: { safePv: ChargingSchedule; efficientPv: ChargingSchedule }): void => {
    mutateEvse.mutate(
      getDtoFn({
        ...evse,
        chargingMode: newMode,
        safePvSchedule: schedules.safePv,
        efficientPvSchedule: schedules.efficientPv,
      } as TEvse)
    )
  }

  const handleSafePvScheduleChange = (schedule: ChargingSchedule): void => {
    mutateEvse.mutate(getDtoFn({ ...evse, chargingMode: 'pvImmediate', safePvSchedule: schedule } as TEvse))
  }

  const handleEfficientPvScheduleChange = (schedule: ChargingSchedule): void => {
    mutateEvse.mutate(getDtoFn({ ...evse, chargingMode: 'pvDelayed', efficientPvSchedule: schedule } as TEvse))
  }

  return {
    isPending: mutateEvse.isPending,
    handleChargingModeChange,
    handleSafePvScheduleChange,
    handleEfficientPvScheduleChange,
  }
}
